import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { getResolveContentQueryOptions, queryClient, useGetSpace } from '@onesource/services';
import { createFileRoute } from '@tanstack/react-router';
import { SubNavPageContainer } from 'src/components/page-container';
import { FeedSearchParamsSchema } from 'src/features/community-hub/components/feed-filter-bar';
import { FeedPostsSkeleton } from 'src/features/community-hub/components/feed-posts-skeleton';
import { SubSpaceCommunityView } from 'src/features/community-hub/components/sub-space-community-view';
import { ContentRouteProvider } from 'src/features/community-hub/hooks/use-content-route';
import { match, P } from 'ts-pattern';

/**
 * Community feed for a FEED sub-space. Path: /community-hub/{programSlug}/{feedSlug}
 * Resolves the slug chain and renders the feed; search params drive the feed filters.
 */
export const Route = createFileRoute('/_protected/community-hub/$programSlug/$feedSlug/')({
  validateSearch: FeedSearchParamsSchema,
  loader: ({ params }) =>
    queryClient.ensureQueryData(
      getResolveContentQueryOptions({ program: params.programSlug, module: params.feedSlug }),
    ),
  component: CommunityFeedRoute,
});

function CommunityFeedRoute() {
  const { programSlug, feedSlug } = Route.useParams();
  const resolved = Route.useLoaderData();
  const feedId = resolved.module?.id ?? '';
  const { data: feedSpace, isLoading, error } = useGetSpace(feedId);

  return (
    // Context lets post cards build slug post URLs (/community-hub/{program}/{feed}/posts/{id}).
    <ContentRouteProvider
      value={{ programId: resolved.program.id, programSlug, subSpaceId: feedId, subSpaceSlug: feedSlug }}
    >
      {/* md+: the feed and sidebar columns scroll internally and fill the viewport,
          so the container's bottom padding would only leave a dead strip. */}
      <SubNavPageContainer sx={{ pb: { md: 0 } }}>
        {match({ isLoading, error, feedSpace })
          .with({ isLoading: true }, () => <FeedPostsSkeleton />)
          .with({ error: P.nonNullable }, ({ error: err }) => (
            <Box p={3}>
              <Alert severity="error">Failed to load community: {err.message}</Alert>
            </Box>
          ))
          .with({ feedSpace: P.nonNullable }, ({ feedSpace: space }) => <SubSpaceCommunityView subSpace={space} />)
          .otherwise(() => (
            <Box p={3}>
              <Alert severity="error">Community not found</Alert>
            </Box>
          ))}
      </SubNavPageContainer>
    </ContentRouteProvider>
  );
}
